import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PropertySearch.module.css';

export interface PropertyOption {
  id: string;
  name: string;
}

interface PropertySearchProps {
  available: PropertyOption[];
  emptyLabel: string;
  onSelect: (propertyId: string) => void;
  onClose: () => void;
}

export const PropertySearch = ({ available, emptyLabel, onSelect, onClose }: PropertySearchProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    if (!wrapperRef.current) return;
    const update = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const trimmed = query.trim();

  const filtered = useMemo(() => {
    if (trimmed.length === 0) return available;
    const q = trimmed.toLowerCase();
    return available.filter((p) => p.name.toLowerCase().includes(q));
  }, [trimmed, available]);

  const showEmptyState = trimmed.length > 0 && filtered.length === 0;
  const showDropdown = rect !== null && (filtered.length > 0 || showEmptyState);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div className={styles.inputBox}>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        />
      </div>
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: 'fixed',
              top: rect.bottom + 4,
              left: rect.left,
              minWidth: rect.width
            }}
          >
            {showEmptyState ? (
              <div className={`${styles.item} ${styles.emptyState}`}>
                {emptyLabel}
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  className={styles.item}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(p.id)}
                >
                  {p.name}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};
